import AccountTreeIcon from "@mui/icons-material/AccountTree";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
  Alert,
  Box,
  Fab,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from "@mui/material";
import DeleteEntityDialog from "components/DeleteEntityDialog";
import Loader from "components/Loader";
import RedirectBackButton from "components/RedirectBackButton";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import useTitle from "hooks/useTitle";
import useTrashablePromises from "hooks/useTrashablePromises";
import { MaterialReactTable } from "material-react-table";
import { useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import {
  ListRequestOfBaseFilter,
  ListResponseOfMenuStructure,
  ResponseCode,
  ResponseOfMenu,
} from "services/KnowfullyAdminClient";
import translateError from "utils/translateError";
import {
  mapToMenuStructureModels,
  menuStructureTableCommonProps,
} from "./menuStructureHelpers";

const MenuDetails = () => {
  const { formatMessage: t } = useIntl();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const {
    menusUrl,
    menuEditUrl,
    menuItemsUrl,
    menuService,
  } = useGlobalContext();
  const [deleteOpen, deleteOpenActions] = useBoolean(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const { registerPromise } = useTrashablePromises();
  const queryClient = useQueryClient();

  const menuResult = useQuery<ResponseOfMenu>(
    ["menu", id],
    () => menuService.getMenuById(id!),
    {
      enabled: !!id,
    }
  );

  const menu = menuResult.data?.data;
  useTitle(menu?.name || "Menu Details");

  const {
    data: menuStructuresData,
    isFetching: isLoadingMenuStructures,
  } = useQuery<ListResponseOfMenuStructure>(
    ["menuStructures", id],
    () =>
      menuService.getMenuStructures(
        id!,
        ListRequestOfBaseFilter.fromJS({
          sortField: "sequence",
          sortDescending: false,
        })
      ),
    {
      enabled: !!id,
    }
  );

  if (!id) return <Navigate to={menusUrl()} replace />;
  if (menuResult.isLoading) return <Loader />;
  if (menuResult.isError) {
    return (
      <Box mt={4}>
        <Alert severity="error">
          <T id="menuLoadError" />
        </Alert>
      </Box>
    );
  }

  const menuStructures = mapToMenuStructureModels(menuStructuresData?.data);

  const handleDelete = async () => {
    try {
      const result = await registerPromise(menuService.deleteMenu(id));

      if (result.responseCode !== ResponseCode.OK) {
        setDeleteError(result.responseReason ?? t({ id: "menuCannotBeDeletedInUse" }));
        deleteOpenActions.setFalse();
        return;
      }

      setDeleteError(null);
      queryClient.invalidateQueries("menus");
      deleteOpenActions.setFalse();
      navigate(menusUrl());
    } catch (error) {
      setDeleteError(translateError(error, t));
      deleteOpenActions.setFalse();
    }
  };

  const columns = [
    {
      accessorKey: "sequence",
      header: t({ id: "sequenceLabel" }),
    },
    {
      accessorKey: "name",
      header: t({ id: "nameLabel" }),
    },
    {
      accessorKey: "menuStructureType",
      header: t({ id: "menuStructureTypeLabel" }),
    },
    {
      accessorKey: "menuItem",
      header: t({ id: "menuItemLabel" }),
    },
  ];

  const rows = [
    {
      title: t({ id: "id" }),
      component: menu?.id && <span>{menu.id}</span>,
    },
    {
      title: t({ id: "ownerOrganization" }),
      component: <span>{menu?.organizationName}</span>,
    },
    {
      title: t({ id: "menuFormNameLabel" }),
      component: menu?.name && <span>{menu.name}</span>,
    },
    {
      title: t({ id: "menuFormDescriptionLabel" }),
      component: menu?.description && <span>{menu.description}</span>,
    },
    {
      title: t({ id: "menuFormMenuTypeLabel" }),
      component: menu?.menuType && <span>{menu.menuType}</span>,
    },
  ].filter((r) => !!r.component);

  return (
    <>
      <RedirectBackButton
        redirectRoute={menusUrl()}
        labelKey="backToListButtonLabel"
        style={{ marginRight: "10px" }}
      />
      <RedirectBackButton
        redirectRoute={menuItemsUrl()}
        labelKey="goToMenuItemsLabel"
        icon={<AccountTreeIcon />}
      />
      <h1>
        <span>{menu?.name}</span>
      </h1>

      <Box my={3} display="flex" justifyContent="center">
        <Link to={menuEditUrl(id)} style={{ textDecoration: "none" }}>
          <Fab color="primary" data-cy="editButton">
            <EditIcon />
          </Fab>
        </Link>
        <Box ml={4} />
        <Fab color="secondary" onClick={deleteOpenActions.setTrue}>
          <DeleteIcon />
        </Fab>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableBody>
            {rows.map(({ title, component }, i) => (
              <TableRow key={i}>
                <TableCell
                  component="th"
                  scope="row"
                  style={{ fontWeight: "bold" }}
                >
                  {title}
                </TableCell>
                <TableCell>{component}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <MaterialReactTable
        {...menuStructureTableCommonProps}
        columns={columns}
        data={menuStructures}
        state={{ isLoading: isLoadingMenuStructures, density: "compact" }}
        renderTopToolbarCustomActions={() => <h3>Structures</h3>}
      />
      {deleteError && (
        <Box my={2}>
          <Alert severity="error" onClose={() => setDeleteError(null)}>
            {deleteError}
          </Alert>
        </Box>
      )}
      <DeleteEntityDialog
        dialogTitleKey="menuDeleteTitle"
        contentKey="menuDeleteInfo"
        onCancel={deleteOpenActions.setFalse}
        handleDelete={handleDelete}
        open={deleteOpen}
      />
    </>
  );
};

export default MenuDetails;
